import { useState } from 'react';
import { Button, Grid, styled, Typography } from '@mui/material';
import { proAccountThemeCoreUI } from '@orcha/micro-library-core-ui';

export type AppDescriptionProps = {
  title?: string;
  descriptionBody?: string;
  showMoreButtonLabel?: string;
  showLessButtonLabel?: string;
};

const HtmlTypography = ({ bodyHtml }: { bodyHtml: string }) => {
  const innerHtml = { __html: bodyHtml ?? '' };

  const FormattedHtml = styled('div')(() => ({
    whiteSpace: 'pre-line',

    '& p': {
      ...proAccountThemeCoreUI.typography.body2,
    },
    '& h2': {
      ...proAccountThemeCoreUI.typography.h6,
    },
    '& h3': {
      ...proAccountThemeCoreUI.typography.h6,
    },
    '& h4': {
      ...proAccountThemeCoreUI.typography.h6,
    },
    '& h5': {
      ...proAccountThemeCoreUI.typography.h6,
    },
    '& h6': {
      ...proAccountThemeCoreUI.typography.h6,
    },
    '& a': {
      textDecoration: 'underline',
    },
  }));

  return <FormattedHtml dangerouslySetInnerHTML={innerHtml} />;
};

/**
 * AppDescription component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {AppDescriptionProps} appDescriptionProps - The props for the AppDescription component
 * @returns {ReactElement} The AppDescription component
 */

export const AppDescription = ({
  title,
  descriptionBody,
  showMoreButtonLabel,
  showLessButtonLabel,
}: AppDescriptionProps) => {
  const [shouldShowFullDescription, setShouldShowFullDescription] =
    useState(false);
  const truncationLength = 1500;

  if (!descriptionBody) {
    return <></>;
  }

  const requiresTruncation = descriptionBody?.length > truncationLength;
  const truncatedBody = requiresTruncation
    ? `${descriptionBody.slice(0, truncationLength - 1)}... `
    : descriptionBody;

  const descriptionToShow = shouldShowFullDescription
    ? descriptionBody
    : truncatedBody;

  const handleButtonClick = () => {
    setShouldShowFullDescription((state) => !state);
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item container direction="column" spacing={1}>
        {title && (
          <Grid item>
            <Typography component="h2" variant="h6">
              {title}
            </Typography>
          </Grid>
        )}

        <Grid item>
          <HtmlTypography bodyHtml={descriptionToShow} />
        </Grid>
      </Grid>
      {requiresTruncation && (
        <Grid item>
          <Button
            sx={{ fontSize: '0.75rem', color: '#E31C79' }}
            variant="text"
            onClick={handleButtonClick}
          >
            {shouldShowFullDescription
              ? showLessButtonLabel
              : showMoreButtonLabel}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
